import React from 'react';
import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  TooltipProps,
  Label,
} from 'recharts';

interface PieChartProps {
  data: {
    name: string;
    value: number;
    color?: string;
  }[];
  height?: number;
  showLegend?: boolean;
  showTooltip?: boolean;
  innerRadius?: number;
  outerRadius?: number;
  donut?: boolean;
  centerLabel?: string;
  centerValue?: string | number;
}

// Actual hex colors for Recharts (CSS variables don't work in SVG fill)
const COLORS = [
  '#171717',  // primary (dark gray)
  '#22c55e',  // green (success)
  '#f97316',  // orange (warning)
  '#ef4444',  // red (error)
  '#8B5CF6',  // purple
  '#EC4899',  // pink
  '#F59E0B',  // amber
];

const CustomTooltip: React.FC<TooltipProps<any, any>> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0];
    return (
      <div className="bg-card border border-border dark:border-border-subtle rounded p-3 shadow-lg">
        <div className="flex items-center gap-2 text-xs">
          <div
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: data.payload.fill }}
          />
          <span className="text-muted-foreground">
            {data.name}:
          </span>
          <span className="font-semibold text-foreground">
            {typeof data.value === 'number' ? data.value.toLocaleString() : data.value}
          </span>
        </div>
      </div>
    );
  }
  return null;
};

// Center label component for donut charts
const CenterLabel = ({ viewBox, value, label }: any) => {
  const { cx, cy } = viewBox;
  return (
    <g>
      <text
        x={cx}
        y={cy - 8}
        textAnchor="middle"
        dominantBaseline="central"
        className="fill-foreground"
        style={{ fontSize: '28px', fontWeight: 'bold' }}
      >
        {value}
      </text>
      {label && (
        <text
          x={cx}
          y={cy + 16}
          textAnchor="middle"
          dominantBaseline="central"
          className="fill-muted-foreground"
          style={{ fontSize: '12px' }}
        >
          {label}
        </text>
      )}
    </g>
  );
};

export const PieChart: React.FC<PieChartProps> = ({
  data,
  height = 300,
  showLegend = true,
  showTooltip = true,
  innerRadius = 0,
  outerRadius = 100,
  donut = false,
  centerLabel,
  centerValue,
}) => {
  const actualInnerRadius = donut ? outerRadius * 0.65 : innerRadius;
  const totalValue = data.reduce((sum, item) => sum + item.value, 0);

  return (
    <ResponsiveContainer width="100%" height={height}>
      <RechartsPieChart>
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          labelLine={false}
          outerRadius={outerRadius}
          innerRadius={actualInnerRadius}
          fill="#8884d8"
          dataKey="value"
          strokeWidth={2}
          stroke="var(--background)"
        >
          {data.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={entry.color || COLORS[index % COLORS.length]}
            />
          ))}
          {donut && (centerValue !== undefined || centerLabel) && (
            <Label
              content={
                <CenterLabel
                  value={centerValue ?? totalValue}
                  label={centerLabel}
                />
              }
              position="center"
            />
          )}
        </Pie>
        {showTooltip && <Tooltip content={<CustomTooltip />} />}
        {showLegend && (
          <Legend
            verticalAlign="bottom"
            height={36}
            wrapperStyle={{
              paddingTop: '12px',
              fontSize: '13px',
            }}
            formatter={(value, entry: any) => (
              <span className="text-foreground">
                {value} ({entry.payload.value})
              </span>
            )}
          />
        )}
      </RechartsPieChart>
    </ResponsiveContainer>
  );
};

